/**
 * Deterministic flattening of a procedural asset document into renderable
 * instances + world-space sockets. Renderer-free: transforms are 16-number
 * column-major matrices (THREE.Matrix4.fromArray-compatible) so the client can
 * consume them directly and the server could re-run this compile bit-identically.
 *
 * Modifier semantics: a node's modifier stack acts in the node's LOCAL frame on
 * the node + its subtree, in order. mirror reflects across the plane through the
 * node origin. A linear array keeps the original and appends count−1 offset
 * copies. A radial array places all count elements (original included) at
 * `radius` from the node origin, rotated about `axis` — step 2π/count for a full
 * circle, sweep/(count−1) when sweep is given. taper/bend cannot run without
 * vertices, so they annotate instances as `deforms`; the client applies them in
 * each instance's local frame (v1 simplification, shared by lab and game).
 * Generated socket copies are suffixed `-mirrored` / `-2`..`-n`, matching
 * assetSocketNames().
 */
import {
  type AssetModifier,
  type AssetNode,
  type AssetVec3,
  type ProceduralAssetV1,
} from "./proceduralAssets.js";
import type { VehicleDef, VehicleHardpointDef, VehicleSeatDef } from "./vehicles.js";
import { DEFAULT_VEHICLE_GLIDE } from "./vehicles.js";
// Portable trig: compile must be bit-identical across JS engines (client vs a
// future server-side recompile), so no engine-approximated Math.* intrinsics.
import * as pmath from "./pmath.js";

export interface CompiledDeforms {
  taper?: { axis: "x" | "y" | "z"; factor: number };
  bend?: { axis: "x" | "y" | "z"; angle: number };
}

export interface CompiledInstance {
  nodeId: string;
  kind: "box" | "cylinder" | "sphere" | "cone" | "capsule" | "torus" | "lathe";
  params: number[];
  profile?: [number, number][];
  faceting?: number;
  /** Column-major 4x4 in asset space (THREE.Matrix4.fromArray-compatible). */
  transform: number[];
  material: number;
  generated: boolean;
  deforms?: CompiledDeforms;
}

export interface CompiledSocket {
  nodeId: string;
  name: string;
  position: AssetVec3;
  forward: AssetVec3;
  up: AssetVec3;
  generated: boolean;
}

export interface CompiledAsset {
  instances: CompiledInstance[];
  sockets: CompiledSocket[];
  bounds: { min: AssetVec3; max: AssetVec3 };
}

type Mat4 = number[]; // 16, column-major

const IDENTITY: Mat4 = [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1];

function mat4Multiply(a: Mat4, b: Mat4): Mat4 {
  const out = new Array<number>(16);
  for (let col = 0; col < 4; col++) {
    for (let row = 0; row < 4; row++) {
      out[col * 4 + row] =
        a[row] * b[col * 4] + a[4 + row] * b[col * 4 + 1]
        + a[8 + row] * b[col * 4 + 2] + a[12 + row] * b[col * 4 + 3];
    }
  }
  return out;
}

/** T * Rx * Ry * Rz * S — matches THREE's Euler order "XYZ". */
function mat4FromTRS(p: AssetVec3, r: AssetVec3, s: AssetVec3): Mat4 {
  const [cx, cy, cz] = [pmath.cos(r[0]), pmath.cos(r[1]), pmath.cos(r[2])];
  const [sx, sy, sz] = [pmath.sin(r[0]), pmath.sin(r[1]), pmath.sin(r[2])];
  // Rotation = Rx*Ry*Rz (column-major columns are the rotated basis vectors).
  const r00 = cy * cz, r01 = -cy * sz, r02 = sy;
  const r10 = cx * sz + sx * sy * cz, r11 = cx * cz - sx * sy * sz, r12 = -sx * cy;
  const r20 = sx * sz - cx * sy * cz, r21 = sx * cz + cx * sy * sz, r22 = cx * cy;
  return [
    r00 * s[0], r10 * s[0], r20 * s[0], 0,
    r01 * s[1], r11 * s[1], r21 * s[1], 0,
    r02 * s[2], r12 * s[2], r22 * s[2], 0,
    p[0], p[1], p[2], 1,
  ];
}

function mat4Translation(v: AssetVec3): Mat4 {
  return [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, v[0], v[1], v[2], 1];
}

function mat4Reflection(axis: "x" | "y" | "z"): Mat4 {
  const m = [...IDENTITY];
  m[axis === "x" ? 0 : axis === "y" ? 5 : 10] = -1;
  return m;
}

function mat4RotationAxis(axis: "x" | "y" | "z", angle: number): Mat4 {
  const c = pmath.cos(angle), s = pmath.sin(angle);
  if (axis === "x") return [1, 0, 0, 0, 0, c, s, 0, 0, -s, c, 0, 0, 0, 0, 1];
  if (axis === "y") return [c, 0, -s, 0, 0, 1, 0, 0, s, 0, c, 0, 0, 0, 0, 1];
  return [c, s, 0, 0, -s, c, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1];
}

function transformPoint(m: Mat4, v: AssetVec3): AssetVec3 {
  return [
    m[0] * v[0] + m[4] * v[1] + m[8] * v[2] + m[12],
    m[1] * v[0] + m[5] * v[1] + m[9] * v[2] + m[13],
    m[2] * v[0] + m[6] * v[1] + m[10] * v[2] + m[14],
  ];
}

function transformDirection(m: Mat4, v: AssetVec3): AssetVec3 {
  const out: AssetVec3 = [
    m[0] * v[0] + m[4] * v[1] + m[8] * v[2],
    m[1] * v[0] + m[5] * v[1] + m[9] * v[2],
    m[2] * v[0] + m[6] * v[1] + m[10] * v[2],
  ];
  // Math.sqrt is exactly rounded (IEEE) and thus portable; Math.hypot is not.
  const len = Math.sqrt(out[0] * out[0] + out[1] * out[1] + out[2] * out[2]) || 1;
  return [out[0] / len, out[1] / len, out[2] / len];
}

/** One primitive or socket flowing through modifier expansion. */
interface Element {
  node: AssetNode;
  transform: Mat4;      // in the frame modifiers are currently acting in
  suffix: string;       // socket-name suffix accumulated from generating modifiers
  generated: boolean;
  deforms?: CompiledDeforms;
}

function applyModifier(elements: Element[], mod: AssetModifier): Element[] {
  if (mod.type === "mirror") {
    const reflect = mat4Reflection(mod.axis);
    return elements.flatMap((e) => [e, {
      ...e,
      transform: mat4Multiply(reflect, e.transform),
      suffix: `${e.suffix}-mirrored`,
      generated: true,
    }]);
  }
  if (mod.type === "array" && mod.mode === "linear") {
    const out: Element[] = [];
    for (const e of elements) {
      out.push(e);
      for (let i = 1; i < mod.count; i++) {
        const offset: AssetVec3 = [mod.offset[0] * i, mod.offset[1] * i, mod.offset[2] * i];
        out.push({ ...e, transform: mat4Multiply(mat4Translation(offset), e.transform), suffix: `${e.suffix}-${i + 1}`, generated: true });
      }
    }
    return out;
  }
  if (mod.type === "array" && mod.mode === "radial") {
    const step = mod.sweep === undefined ? (Math.PI * 2) / mod.count : mod.sweep / (mod.count - 1);
    // radius runs along the reference perpendicular: +x for axis y/z, +y for axis x
    const radiusVec: AssetVec3 = mod.axis === "x" ? [0, mod.radius, 0] : [mod.radius, 0, 0];
    const lift = mat4Translation(radiusVec);
    const out: Element[] = [];
    for (const e of elements) {
      for (let i = 0; i < mod.count; i++) {
        const placed = mat4Multiply(mat4RotationAxis(mod.axis, step * i), mat4Multiply(lift, e.transform));
        out.push(i === 0
          ? { ...e, transform: placed }
          : { ...e, transform: placed, suffix: `${e.suffix}-${i + 1}`, generated: true });
      }
    }
    return out;
  }
  // taper / bend: annotate primitives; sockets pass through untouched
  return elements.map((e) => e.node.kind === "socket" ? e : {
    ...e,
    deforms: mod.type === "taper"
      ? { ...e.deforms, taper: { axis: mod.axis, factor: mod.factor } }
      : { ...e.deforms, bend: { axis: mod.axis, angle: mod.angle } },
  });
}

/** Compile a subtree; returned transforms are in the PARENT's frame. */
function compileNodes(nodes: AssetNode[]): Element[] {
  const out: Element[] = [];
  for (const node of nodes) {
    // Build the node's own set in ITS local frame (identity at the node origin).
    let set: Element[] = [];
    if (node.kind !== "group") {
      set.push({ node, transform: [...IDENTITY], suffix: "", generated: false });
    }
    if (node.children?.length) set.push(...compileNodes(node.children));
    for (const mod of node.modifiers ?? []) set = applyModifier(set, mod);
    // Map into the parent frame through the node's own TRS.
    const local = mat4FromTRS(node.p, node.r, node.s ?? [1, 1, 1]);
    for (const e of set) out.push({ ...e, transform: mat4Multiply(local, e.transform) });
  }
  return out;
}

/** Conservative local half-extents per primitive (deforms ignored — bounds are
 * advisory framing data, not collision). */
function halfExtents(node: AssetNode): AssetVec3 {
  const p = node.params ?? [];
  switch (node.kind) {
    case "box": return [p[0] / 2, p[1] / 2, p[2] / 2];
    case "cylinder": { const r = Math.max(p[0], p[1]); return [r, p[2] / 2, r]; }
    case "sphere": return [p[0], p[0], p[0]];
    case "cone": return [p[0], p[1] / 2, p[0]];
    case "capsule": return [p[0], p[1] / 2 + p[0], p[0]];
    case "torus": return [p[0] + p[1], p[1], p[0] + p[1]];
    case "lathe": {
      const radius = Math.max(...(node.profile ?? [[0, 0]]).map((pt) => pt[0]));
      const y = Math.max(...(node.profile ?? [[0, 0]]).map((pt) => Math.abs(pt[1])));
      return [radius, y, radius];
    }
    default: return [0, 0, 0];
  }
}

export function compileAsset(asset: ProceduralAssetV1): CompiledAsset {
  const elements = compileNodes(asset.nodes);
  const instances: CompiledInstance[] = [];
  const sockets: CompiledSocket[] = [];
  const min: AssetVec3 = [Infinity, Infinity, Infinity];
  const max: AssetVec3 = [-Infinity, -Infinity, -Infinity];
  for (const e of elements) {
    if (e.node.kind === "socket") {
      sockets.push({
        nodeId: e.node.id,
        name: (e.node.name ?? e.node.id) + e.suffix,
        position: transformPoint(e.transform, [0, 0, 0]),
        forward: transformDirection(e.transform, [0, 0, -1]),
        up: transformDirection(e.transform, [0, 1, 0]),
        generated: e.generated,
      });
      continue;
    }
    const instance: CompiledInstance = {
      nodeId: e.node.id,
      kind: e.node.kind as CompiledInstance["kind"],
      params: [...(e.node.params ?? [])],
      transform: e.transform,
      material: e.node.material ?? 0,
      generated: e.generated,
    };
    if (e.node.profile) instance.profile = e.node.profile.map((pt) => [pt[0], pt[1]] as [number, number]);
    if (e.node.faceting !== undefined) instance.faceting = e.node.faceting;
    if (e.deforms) instance.deforms = e.deforms;
    instances.push(instance);
    const h = halfExtents(e.node);
    for (const sx of [-1, 1]) for (const sy of [-1, 1]) for (const sz of [-1, 1]) {
      const corner = transformPoint(e.transform, [h[0] * sx, h[1] * sy, h[2] * sz]);
      for (let axis = 0; axis < 3; axis++) {
        if (corner[axis] < min[axis]) min[axis] = corner[axis];
        if (corner[axis] > max[axis]) max[axis] = corner[axis];
      }
    }
  }
  if (!instances.length) { min.fill(0); max.fill(0); }
  return { instances, sockets, bounds: { min, max } };
}

/** Merge the vehicle gameplay block with compiled socket transforms into a real
 * VehicleDef. Callers validate with proceduralAssetIssues first; a missing block
 * or socket here is a programming error and throws. */
export function compileVehicleDef(asset: ProceduralAssetV1): VehicleDef {
  const data = asset.vehicle;
  if (!data) throw new Error(`asset ${asset.id} has no vehicle block`);
  const compiled = compileAsset(asset);
  const bySocket = new Map(compiled.sockets.map((socket) => [socket.name, socket]));
  const socket = (name: string, label: string): CompiledSocket => {
    const found = bySocket.get(name);
    if (!found) throw new Error(`asset ${asset.id}: ${label} socket ${name} missing`);
    return found;
  };
  const seats: VehicleSeatDef[] = data.seats.map((seat) => {
    const out: VehicleSeatDef = {
      id: seat.id,
      role: seat.role,
      position: socket(seat.seatSocket, `seat ${seat.id}`).position,
      exit: socket(seat.exitSocket, `seat ${seat.id} exit`).position,
    };
    if (seat.hardpoints) (out as { hardpoints?: readonly string[] }).hardpoints = [...seat.hardpoints];
    if (seat.riderPose) (out as { riderPose?: typeof seat.riderPose }).riderPose = seat.riderPose;
    if (seat.entrySocket) {
      (out as { transition?: VehicleSeatDef["transition"] }).transition = {
        entry: socket(seat.entrySocket, `seat ${seat.id} entry`).position,
        mountDuration: seat.mountDuration ?? 1.2,
        dismountDuration: seat.dismountDuration ?? 0.9,
        approachFraction: 0.35,
        arcHeight: seat.arcHeight ?? 0.12,
      };
    }
    return out;
  });
  const hardpoints: VehicleHardpointDef[] = data.hardpoints.map((hp) => {
    const s = socket(hp.socket, "hardpoint");
    const out: VehicleHardpointDef = { id: hp.socket, kind: hp.kind, position: s.position, direction: s.forward };
    if (hp.group !== undefined) (out as { group?: 1 | 2 }).group = hp.group;
    if (hp.weapon !== undefined) (out as { weapon?: string }).weapon = hp.weapon;
    return out;
  });
  const def: VehicleDef = {
    id: asset.id,
    name: asset.name,
    drive: data.drive,
    mass: data.mass,
    inertia: data.inertia,
    probes: data.probes.map((probe) => ({ ...probe, position: [...probe.position] as AssetVec3 })),
    handling: { ...data.handling },
    // AssetVehicleData carries no glide block yet; compiled vehicles get the
    // shared default so they are valid + flyable (see DEFAULT_VEHICLE_GLIDE).
    glide: { ...DEFAULT_VEHICLE_GLIDE },
    collider: { ...data.collider },
    seats,
    hardpoints,
    weapons: data.weapons.map((weapon) => ({ ...weapon })),
    camera: { ...data.camera },
  };
  if (data.hover) (def as { hover?: VehicleDef["hover"] }).hover = { ...data.hover };
  if (data.boost) (def as { boost?: VehicleDef["boost"] }).boost = { ...data.boost };
  return def;
}
